import "./styles.css";
import "flatpickr/dist/flatpickr.min.css";
import "./legal.css";
import "./improvements.css";
import "./pis-screen.css";
import metroIconUrl from "./assets/icons/metro.svg";
import stationIconUrl from "./assets/icons/station.svg";
import line4Raw from "./data/line4.geojson?raw";
import line6Raw from "./data/line6.geojson?raw";
import { T0402_TIMETABLE } from "./data/t0402-timetable.ts";
import { readRuntimeConfig } from "./config/runtime.ts";
import { buildRoute } from "./domain/geometry.ts";
import { parseLineData } from "./domain/map-data.ts";
import { calculateStationArrivals } from "./domain/station-arrivals.ts";
import {
  SCHEDULE_MODE_STORAGE_KEY,
  isScheduleSelection,
  isScheduleSelectionMode,
  resolveActiveSchedules,
  nonOperatingStationIds,
  type RuntimeScheduleId,
  type ScheduleSelection,
} from "./domain/schedule-selection.ts";
import { shanghaiDaySeconds, type DomainTimetable } from "./domain/timetable.ts";
import { calculateTrainStates } from "./domain/train-state.ts";
import { createBaseLayerController } from "./map/base-layer-control.ts";
import { createRouteLayer } from "./map/route-layer.ts";
import { createStationLayer } from "./map/station-layer.ts";
import { createTrainLayer } from "./map/train-layer.ts";
import { loadAmap } from "./services/amap.ts";
import { readClock } from "./services/clock.ts";
import { SimulationClock } from "./services/simulation-clock.ts";
import { mountDialogControl } from "./ui/dialog-control.ts";
import { mountDetailDialog } from "./ui/detail-dialog.ts";
import { mountMapControls, type StatusPanelMode } from "./ui/map-controls.ts";
import { mountScheduleControls } from "./ui/schedule-controls.ts";
import { mountSimulationControls } from "./ui/simulation-controls.ts";
import { renderStatusPanel } from "./ui/status-panel.ts";
import { renderPisStation, renderPisTrain } from "./ui/pis-screen.ts";
import { createNetworkMap, isNetworkTrainDisplayable } from "./ui/network-map.ts";
import type { Point } from "./ui/network-map-projection.ts";

const root = document.querySelector<HTMLElement>("#app");
if (!root) throw new Error("Missing #app root");
root.innerHTML = `<header><div class="brand"><p class="eyebrow">AMTR · 4号线 / 6号线</p><h1>AMTR 新线试运行实时演示系统</h1></div><nav class="header-actions" aria-label="显示与时间控制"><button type="button" data-dialog-trigger="schedule-dialog" aria-haspopup="dialog" aria-controls="schedule-dialog">运行图</button><button type="button" data-dialog-trigger="map-settings-dialog" aria-haspopup="dialog" aria-controls="map-settings-dialog">地图设置</button><button type="button" data-dialog-trigger="simulation-dialog" aria-haspopup="dialog" aria-controls="simulation-dialog">时间控制</button></nav><div class="disclaimer"><b>模拟位置</b><span>非 GPS 实时位置 · 非运营方实时数据</span><small id="clock-source">正在校时</small></div></header><dialog id="schedule-dialog" class="control-dialog schedule-dialog" aria-labelledby="schedule-title"><section class="dialog-card"><div class="dialog-heading"><h2 id="schedule-title">运行图切换</h2><button type="button" data-close-dialog aria-label="关闭运行图切换">×</button></div><div id="schedule-controls" class="schedule-controls" aria-label="运行图切换设置"></div></section></dialog><dialog id="map-settings-dialog" class="control-dialog map-dialog" aria-labelledby="map-settings-title"><section class="dialog-card"><div class="dialog-heading"><h2 id="map-settings-title">地图与列车显示</h2><button type="button" data-close-dialog aria-label="关闭地图设置">×</button></div><div id="map-controls" class="map-toolbar" aria-label="地图显示设置"><p class="dialog-loading">正在加载地图设置…</p></div></section></dialog><dialog id="simulation-dialog" class="control-dialog simulation-dialog" aria-labelledby="simulation-title"><section class="dialog-card"><div class="dialog-heading"><h2 id="simulation-title">时间与模拟控制</h2><button type="button" data-close-dialog aria-label="关闭时间控制">×</button></div><div id="simulation" class="controls" aria-label="模拟控制"></div></section></dialog><main class="layout"><section class="map-card"><div class="map-stage"><div id="map" aria-label="AMTR 4号线与6号线实际地图"><div class="map-loading">正在加载地图…</div></div><div id="network-map" aria-label="厦门地铁1、2、3、4、6、9号线线网拓扑图" hidden></div></div><div class="site-watermark" aria-hidden="true">© BG2FOU</div><details class="route-legend-module"><summary>图例</summary><div class="route-legend" aria-label="图例"><span><i class="line4" aria-hidden="true"></i>4号线</span><span><i class="line6" aria-hidden="true"></i>6号线</span><span><i class="elevated" aria-hidden="true"></i>高架段</span><span><i class="underground" aria-hidden="true"></i>隧道段</span><span><img src="${stationIconUrl}" alt="" aria-hidden="true">车站</span><span><img src="${metroIconUrl}" alt="" aria-hidden="true">列车</span></div></details><div id="map-error" class="map-error" hidden></div></section><aside id="side-dock" class="side-dock" hidden><section id="status-panel" class="panel"><h2>列车状态</h2><div id="status" class="states" aria-live="polite"></div></section></aside></main><footer><span class="footer-version">线路数据 L4/L6 · 运行图正在加载 · Asia/Shanghai</span><p class="legal">COPYRIGHT © <a href="https://github.com/BG2FOU" target="_blank" rel="noopener noreferrer"><strong>BG2FOU</strong></a> | <a href="https://beian.miit.gov.cn/" target="_blank" rel="noopener noreferrer">闽ICP备2025114531号</a></p></footer>`;

root.insertAdjacentHTML("beforeend", `<dialog id="detail-dialog" class="detail-dialog" aria-labelledby="detail-dialog-title"><div class="detail-dialog-frame"><div class="detail-dialog-heading"><span id="detail-dialog-title">运行信息</span><button type="button" class="detail-dialog-close" data-close-detail aria-label="关闭信息">×</button></div><div class="detail-dialog-content"></div></div></dialog>`);

const dialogDisposers = [
  ["schedule-dialog", "schedule-dialog"],
  ["map-settings-dialog", "map-settings-dialog"],
  ["simulation-dialog", "simulation-dialog"],
].map(([dialogId, triggerId]) => mountDialogControl(
  document.querySelector<HTMLDialogElement>(`#${dialogId}`)!,
  document.querySelector<HTMLButtonElement>(`[data-dialog-trigger="${triggerId}"]`)!,
));

const line4 = parseLineData(JSON.parse(line4Raw), "AMTR-L4");
const line6 = parseLineData(JSON.parse(line6Raw), "AMTR-L6");
const allStations = [...line4.stations, ...line6.stations];
const depotStationIds = new Set(allStations.filter((station) => station.stationType === "depot").map((station) => station.id));
const mapElement = document.querySelector<HTMLElement>("#map")!;
const networkElement = document.querySelector<HTMLElement>("#network-map")!;
const mapError = document.querySelector<HTMLElement>("#map-error")!;
const line4Route = buildRoute(line4);
const line6Route = buildRoute(line6);
const emptyTimetable: DomainTimetable = { trips: [], circulations: [], conventions: { serviceDayRollover: "03:00:00" } };
const timetableCache = new Map<RuntimeScheduleId, DomainTimetable>([["T0402", T0402_TIMETABLE as unknown as DomainTimetable]]);
const timetableLoads = new Map<RuntimeScheduleId, Promise<DomainTimetable>>();
let line4Timetable = timetableCache.get("T0402")!;
let line6Timetable = emptyTimetable;
let displayMode: "map" | "network" = "map";
let mapUnavailable = false;
let selectedTrainKey: string | undefined;
let selectedStationId: string | undefined;
let latestNow = new Date();
let latestNonOperating: ReadonlySet<string> = new Set();
let latestScheduleIds: { line4: string; line6: string } = { line4: "T0402", line6: "G0601" };
const detailDialog = mountDetailDialog(document.querySelector<HTMLDialogElement>("#detail-dialog")!, () => {
  selectedTrainKey = undefined;
  selectedStationId = undefined;
});

const trainKey = (state: ReturnType<typeof calculateTrainStates>[number]): string => `${state.lineId ?? "AMTR-L4"}:${state.vehicleId}`;
const stationTimetable = (station: (typeof allStations)[number]): DomainTimetable => station.lineId === "AMTR-L6" ? line6Timetable : line4Timetable;
const pisMode = () => displayMode === "map" ? "A" : "B";
const stationScreen = (station: (typeof allStations)[number]): string => renderPisStation(station, calculateStationArrivals(latestDaySeconds, station.id, stationTimetable(station)), allStations, pisMode(), station.lineId === "AMTR-L6" ? latestScheduleIds.line6 : latestScheduleIds.line4, latestNow);
const trainScreen = (state: ReturnType<typeof calculateTrainStates>[number]): string => renderPisTrain(state, state.lineId === "AMTR-L6" ? line6 : line4, state.lineId === "AMTR-L6" ? line6Timetable : line4Timetable, allStations, pisMode(), latestDaySeconds, latestNonOperating);

function showTrainDetail(state: ReturnType<typeof calculateTrainStates>[number]): void {
  selectedTrainKey = trainKey(state);
  selectedStationId = undefined;
  detailDialog.show(trainScreen(state), "train");
}

function showNetworkTrain(state: ReturnType<typeof calculateTrainStates>[number], _anchor: Point): void {
  showTrainDetail(state);
}

function showNetworkStation(station: (typeof allStations)[number], _anchor: Point): void {
  selectedTrainKey = undefined;
  selectedStationId = station.id;
  detailDialog.show(stationScreen(station), "station");
}

const networkMap = createNetworkMap(networkElement, [line4, line6], {
  routes: new Map([["AMTR-L4", line4Route], ["AMTR-L6", line6Route]]),
  onTrainSelect: showNetworkTrain,
  onStationSelect: showNetworkStation,
});

const setDisplayMode = (mode: "map" | "network"): void => {
  detailDialog.close();
  displayMode = mode;
  const scheduleTrigger = document.querySelector<HTMLButtonElement>('[data-dialog-trigger="schedule-dialog"]')!;
  scheduleTrigger.hidden = mode === "network";
  if (mode === "network") {
    const scheduleDialog = document.querySelector<HTMLDialogElement>("#schedule-dialog")!;
    if (scheduleDialog.open) scheduleDialog.close();
    if (scheduleMode !== "auto") {
      scheduleMode = "auto";
      saveScheduleSelection(scheduleMode);
      update();
    }
  }
  document.querySelector<HTMLElement>(".footer-version")!.hidden = mode === "network";
  renderSidebar();
  mapElement.hidden = mode !== "map";
  networkMap.setVisible(mode === "network");
  const geographicLegend = document.querySelector<HTMLElement>(".route-legend-module");
  if (geographicLegend) geographicLegend.hidden = mode === "network";
  mapError.hidden = mode === "network" || !mapUnavailable;
  document.querySelectorAll<HTMLButtonElement>("[data-view-mode]").forEach((button) => button.setAttribute("aria-pressed", String(button.dataset.viewMode === mode)));
};
const runtime = readRuntimeConfig({
  ...import.meta.env,
  ...(!import.meta.env.PROD && __AMTR_LOCAL_AMAP_KEY__ ? { VITE_AMAP_API_KEY: __AMTR_LOCAL_AMAP_KEY__ } : {}),
  ...(!import.meta.env.PROD && __AMTR_LOCAL_AMAP_SECURITY_JS_CODE__ ? { AMAP_SECURITY_JS_CODE: __AMTR_LOCAL_AMAP_SECURITY_JS_CODE__ } : {}),
});
let clockOffset = 0;
const clock = new SimulationClock(() => Date.now() + clockOffset);
const readScheduleSelection = (): ScheduleSelection => {
  try {
    const value = window.localStorage.getItem(SCHEDULE_MODE_STORAGE_KEY);
    if (isScheduleSelectionMode(value)) return value;
    if (value) {
      const parsed: unknown = JSON.parse(value);
      if (isScheduleSelection(parsed)) return parsed;
    }
    return "auto";
  } catch {
    return "auto";
  }
};
const saveScheduleSelection = (selection: ScheduleSelection): void => {
  try {
    window.localStorage.setItem(SCHEDULE_MODE_STORAGE_KEY, typeof selection === "object" ? JSON.stringify(selection) : selection);
  } catch { /* Storage can be unavailable in privacy modes. */ }
};
let scheduleMode = readScheduleSelection();
const status = document.querySelector<HTMLElement>("#status")!;
const sideDock = document.querySelector<HTMLElement>("#side-dock")!;
const layout = document.querySelector<HTMLElement>(".layout")!;
let latestStates: ReturnType<typeof calculateTrainStates> = [];
function renderSidebar(): void {
  renderStatusPanel(status, displayMode === "network" ? latestStates.filter((state) => isNetworkTrainDisplayable(state, depotStationIds)) : latestStates, allStations);
}
const setStatusPanelMode = (mode: StatusPanelMode): void => {
  const visible = mode === "visible";
  sideDock.hidden = !visible;
  layout.classList.toggle("sidebar-open", visible);
};
const onStatusClick = (event: MouseEvent): void => {
  const target = event.target instanceof Element ? event.target.closest<HTMLElement>("[data-train-key]") : null;
  const state = latestStates.find((candidate) => trainKey(candidate) === target?.dataset.trainKey);
  if (state) showTrainDetail(state);
};
status.addEventListener("click", onStatusClick);
let latestDaySeconds = 0;
let updateMap: ((states: ReturnType<typeof calculateTrainStates>) => void) | undefined;
let refreshSelectedTrain: ((states: ReturnType<typeof calculateTrainStates>) => void) | undefined;
let updateStationServiceStatus: ((stationIds: ReadonlySet<string>) => void) | undefined;

async function loadRuntimeTimetable(scheduleId: RuntimeScheduleId): Promise<DomainTimetable> {
  switch (scheduleId) {
    case "T0402": return T0402_TIMETABLE as unknown as DomainTimetable;
    case "T0403": return (await import("./data/runtime/t0403.json")).default as unknown as DomainTimetable;
    case "T0404": return (await import("./data/runtime/t0404.json")).default as unknown as DomainTimetable;
    case "T0405": return (await import("./data/runtime/t0405.json")).default as unknown as DomainTimetable;
    case "G0601": return (await import("./data/runtime/g0601.json")).default as unknown as DomainTimetable;
    case "G0602": return (await import("./data/runtime/g0602.json")).default as unknown as DomainTimetable;
    case "S0601": return (await import("./data/runtime/s0601.json")).default as unknown as DomainTimetable;
    case "S0602": return (await import("./data/runtime/s0602.json")).default as unknown as DomainTimetable;
    case "T0601": return (await import("./data/runtime/t0601.json")).default as unknown as DomainTimetable;
  }
}

function ensureTimetable(scheduleId: RuntimeScheduleId): void {
  if (timetableCache.has(scheduleId) || timetableLoads.has(scheduleId)) return;
  const load = loadRuntimeTimetable(scheduleId)
    .then((timetable) => {
      timetableCache.set(scheduleId, timetable);
      timetableLoads.delete(scheduleId);
      update();
      return timetable;
    })
    .catch((error: unknown) => {
      timetableLoads.delete(scheduleId);
      console.error(`Unable to load ${scheduleId} runtime timetable`, error);
      throw error;
    });
  timetableLoads.set(scheduleId, load);
  void load.catch(() => undefined);
}

const scheduleControls = mountScheduleControls(
  document.querySelector<HTMLElement>("#schedule-controls")!,
  scheduleMode,
  (mode) => {
    scheduleMode = mode;
    saveScheduleSelection(mode);
    update();
  },
);

function update(): void {
  const now = clock.snapshot().now;
  const daySeconds = shanghaiDaySeconds(now);
  const schedules = resolveActiveSchedules(scheduleMode, now);
  line4Timetable = timetableCache.get(schedules.line4) ?? emptyTimetable;
  line6Timetable = timetableCache.get(schedules.line6) ?? emptyTimetable;
  ensureTimetable(schedules.line4);
  ensureTimetable(schedules.line6);
  scheduleControls.update(scheduleMode, schedules);
  const nonOperating = nonOperatingStationIds(schedules);
  latestNow = now;
  latestScheduleIds = schedules;
  latestNonOperating = nonOperating;
  updateStationServiceStatus?.(nonOperating);
  networkMap.setNonOperatingStations(nonOperating);
  const footerVersion = document.querySelector<HTMLElement>(".footer-version");
  if (footerVersion) footerVersion.textContent = `线路数据 L4/L6 · 运行图 ${schedules.line4}/${schedules.line6} · Asia/Shanghai`;
  latestDaySeconds = daySeconds;
  latestStates = [
    ...calculateTrainStates(daySeconds, line4Timetable, line4Route, "AMTR-L4", "#E71419"),
    ...calculateTrainStates(daySeconds, line6Timetable, line6Route, "AMTR-L6", "#BA28B9"),
  ];
  renderSidebar();
  networkMap.update(latestStates);
  updateMap?.(latestStates);
  refreshSelectedTrain?.(latestStates);
  if (displayMode === "network" && detailDialog.isOpen()) {
    if (selectedStationId) {
      const station = allStations.find((candidate) => candidate.id === selectedStationId);
      if (station) detailDialog.refresh(stationScreen(station));
    } else if (selectedTrainKey) {
      const state = latestStates.find((candidate) => trainKey(candidate) === selectedTrainKey);
      if (state) detailDialog.refresh(trainScreen(state));
      else detailDialog.close();
    }
  }
}

const destroySimulationControls = mountSimulationControls(
  document.querySelector<HTMLElement>("#simulation")!,
  clock,
  update,
  (now) => resolveActiveSchedules(scheduleMode, now).line6,
);
update();
const timer = window.setInterval(update, 1_000);

async function synchronizeClock(): Promise<void> {
  const reading = await readClock({ ...(runtime.timeApiUrl ? { timeApiUrl: runtime.timeApiUrl } : {}) });
  clockOffset = reading.now.getTime() - Date.now();
  const source = document.querySelector<HTMLElement>("#clock-source");
  if (source) source.textContent = reading.source === "json-api" ? "时间源：远程校时" : reading.source === "http-date" ? "时间源：站点 HTTP Date" : "时间源：设备时间（降级）";
  update();
}
void synchronizeClock();
const syncTimer = window.setInterval(() => void synchronizeClock(), 300_000);
const onVisibility = () => { if (document.visibilityState === "visible") void synchronizeClock(); };
document.addEventListener("visibilitychange", onVisibility);

const describeError = (error: unknown): string => {
  if (error instanceof Error) return error.message;
  if (typeof error === "string") return error;
  if (error && typeof error === "object" && "message" in error && typeof error.message === "string") return error.message;
  return "未知错误";
};
loadAmap(runtime).then((api) => {
  const map = new api.Map(mapElement, { zoom: 11, center: [118.19, 24.6], viewMode: "2D", resizeEnable: true });
  const syncMapZoom = () => { mapElement.dataset.mapZoom = String(map.getZoom?.() ?? 11); };
  const setTestZoom = (event: Event) => { if (!import.meta.env.PROD && event instanceof CustomEvent && typeof event.detail === "number") map.setZoom?.(event.detail); };
  map.on?.("zoomchange", syncMapZoom);
  if (!import.meta.env.PROD) mapElement.addEventListener("amtr:set-test-zoom", setTestZoom);
  syncMapZoom();
  const baseLayers = createBaseLayerController(api, map);
  const routeLayers = [createRouteLayer(api, map, line4), createRouteLayer(api, map, line6)];
  const showTrain = (state: (typeof latestStates)[number]): void => {
    if (state.position) showTrainDetail(state);
  };
  const showStation = (station: (typeof allStations)[number]): void => {
    selectedTrainKey = undefined;
    selectedStationId = station.id;
    detailDialog.show(stationScreen(station), "station");
  };
  const showTestStation = (event: Event): void => {
    if (import.meta.env.PROD || !(event instanceof CustomEvent) || typeof event.detail !== "string") return;
    const station = allStations.find((candidate) => candidate.id === event.detail);
    if (station) showStation(station);
  };
  if (!import.meta.env.PROD) mapElement.addEventListener("amtr:show-station", showTestStation);
  const stationLayers = [createStationLayer(api, map, line4, showStation), createStationLayer(api, map, line6, showStation)];
  updateStationServiceStatus = (stationIds) => stationLayers.forEach((layer) => layer.setNonOperatingStations(stationIds));
  const trains = createTrainLayer(api, map, showTrain, allStations);
  const setLineVisible = (lineId: "AMTR-L4" | "AMTR-L6", visible: boolean): void => {
    const index = lineId === "AMTR-L4" ? 0 : 1;
    routeLayers[index]?.setVisible(visible);
    stationLayers[index]?.setVisible(visible);
    trains.setLineVisible(lineId, visible);
    networkMap.setLineVisible(lineId, visible);
  };
  const destroyMapControls = mountMapControls(document.querySelector<HTMLElement>("#map-controls")!, baseLayers, setStatusPanelMode, setLineVisible, setDisplayMode);
  updateMap = (states) => trains.update(states);
  refreshSelectedTrain = (states) => {
    if (!detailDialog.isOpen() || displayMode !== "map") return;
    if (selectedStationId) {
      const station = allStations.find((candidate) => candidate.id === selectedStationId);
      if (station) detailDialog.refresh(stationScreen(station));
      return;
    }
    if (!selectedTrainKey) return;
    const state = states.find((candidate) => trainKey(candidate) === selectedTrainKey);
    if (state?.position) detailDialog.refresh(trainScreen(state));
    else detailDialog.close();
  };
  update();
  map.setFitView(stationLayers.flatMap((layer) => [...layer.overlays]));
  document.querySelector<HTMLElement>(".map-loading")?.remove();
  window.addEventListener("pagehide", () => { updateStationServiceStatus = undefined; destroyMapControls(); trains.destroy(); stationLayers.forEach((layer) => layer.destroy()); routeLayers.forEach((destroy) => destroy()); map.off?.("zoomchange", syncMapZoom); mapElement.removeEventListener("amtr:set-test-zoom", setTestZoom); mapElement.removeEventListener("amtr:show-station", showTestStation); networkMap.destroy(); map.destroy(); }, { once: true });
}).catch((error: unknown) => {
  mapUnavailable = true;
  document.querySelector<HTMLElement>(".map-loading")?.remove();
  const mapControls = document.querySelector<HTMLElement>("#map-controls");
  if (mapControls) mountMapControls(mapControls, undefined, setStatusPanelMode, (lineId, visible) => networkMap.setLineVisible(lineId, visible), setDisplayMode);
  mapError.hidden = displayMode === "network";
  mapError.textContent = `地图暂不可用：${describeError(error)}。时间模拟和列车推演仍可使用。`;
});

window.addEventListener("pagehide", () => {
  window.clearInterval(timer);
  window.clearInterval(syncTimer);
  document.removeEventListener("visibilitychange", onVisibility);
  status.removeEventListener("click", onStatusClick);
  detailDialog.destroy();
  dialogDisposers.forEach((dispose) => dispose());
  scheduleControls.destroy();
  destroySimulationControls();
}, { once: true });
